import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useReducer, useState } from 'react'
import { Container } from '../../components/Animations'
import { PrimaryButton } from '../../components/Button'
import ConfirmButtonModal from '../../components/ConfirmButtonModal'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import PageHeader from '../../components/PageHeader'
import Table from '../../components/Table'
import TimeText from '../../components/TimeText'
import Dots from '../../icons/Dots'
import Radom, { WithdrawalAccount } from '../../state/Radom'
import { getMethod } from '../../util/Managed'
import { infoToast, successToast, useTestMode } from '../../util/Util'
import CreateWithdrawalAccountModal from './CreateWithdrawalAccountModal'
import { formatWithdrawalAccount, formatWithdrawalAccountType } from '../../components/Shared'

const Accounts = observer((): ReactElement => {
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateWithdrawalModalOpen, setIsCreateWithdrawalModalOpen] = useState(false)
  const [withdrawalAccounts, setWithdrawalAccounts] = useState<WithdrawalAccount[]>([])
  const [testMode] = useTestMode()
  const [closeDropdownFns, updateCloseDropdownFns] = useReducer((p: any, n: any) => ({ ...p, ...n }), {})

  const fetchWithdrawalAccounts = async (): Promise<void> => {
    setIsLoading(true)
    setWithdrawalAccounts([])
    Radom.listWithdrawalAccounts()
      .then((accs) => setWithdrawalAccounts(accs))
      .finally(() => setIsLoading(false)
      )
  }

  useEffect(() => {
    fetchWithdrawalAccounts()
  }, [testMode])

  return <Container>
    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', rowGap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageHeader title='Withdrawal Accounts' />
          <PrimaryButton style={{ gap: 8 }} onClick={() => {
            setIsCreateWithdrawalModalOpen(true)
          }}>
            <span style={{ fontSize: 20, marginTop: -3 }}>+</span>
            <span>Create withdrawal account</span>
          </PrimaryButton>
        </div>
      </div>

      <Table
        borderless
        headers={['Account', 'Type', 'Details', 'Created at', ''].map(t => <span key={t}>{t}</span>)}
        items={
          withdrawalAccounts
            .filter(t => testMode
              ? getMethod(t.accountDetails.Crypto?.network ?? '').isTestnet
              : !getMethod(t.accountDetails.Crypto?.network ?? '').isTestnet
            ).map(a => [
              <div key={0} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span>{a.name}</span>
                <span style={{ opacity: 0.5, fontSize: 14 }}>{a.id}</span>
              </div>,
              <div key={2}>
                {formatWithdrawalAccountType(a.accountDetails)}
              </div>,
              <div key={3}>
                {formatWithdrawalAccount(a.accountDetails)}
              </div>,
              <TimeText key="txTime" date={new Date(a.createdAt)} />,
              <div key={a.id} onClick={e => e.stopPropagation()}>

                <Dropdown
                  noChevron
                  onCloseFn={f => updateCloseDropdownFns({ [a.id]: f })}
                  selectedContent={<Dots width={15} />}
                  dropdownContent={
                    <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                      <DropdownItem onClick={() => {
                        navigator.clipboard.writeText(a.id)
                        infoToast('Account ID copied to clipboard')
                        closeDropdownFns[a.id]()
                      }}>Copy account ID</DropdownItem>
                      <ConfirmButtonModal
                        title='Delete account'
                        description='Are you sure you want to delete this account?'
                        action='Delete account'
                        onConfirm={async () => {
                          Radom.deleteWithdrawalAccount(a.id)
                            .then(async () => await fetchWithdrawalAccounts())
                            .then(() => successToast('Account deleted'))
                        }}
                        ActionComponent={(props: any) => <DropdownItem {...props}>Delete account</DropdownItem>}
                      />
                    </div>

                  }
                />
              </div>
            ])
        }
        emptyPlaceholderText='No accounts'
        isLoading={isLoading} />

      {
        isCreateWithdrawalModalOpen &&
        <CreateWithdrawalAccountModal
          title='Create withdrawal account'
          visible={isCreateWithdrawalModalOpen}
          onClose={() => setIsCreateWithdrawalModalOpen(false)}
          onAccountCreated={() => {
            setIsCreateWithdrawalModalOpen(false)
            fetchWithdrawalAccounts()
          }} />
      }
    </div>
  </Container>
})

export default Accounts
